import type { PaletteInstance } from '../art/types';
import { COAT_BAY } from '../art/palettes';
import { DerbyClock } from '../sim/fixed-step';
import {
  DRILL_TICK_S,
  MASK_HELD,
  MASK_LEFT,
  MASK_LEFT_PRESSED,
  MASK_PRESSED,
  MASK_RIGHT,
  MASK_RIGHT_PRESSED,
  type MinigameSession,
  type SessionMods,
} from '../sim/minigames/framework';
import { el } from '../ui/dom';

/** What a training scene receives from the sim's escrowed session. */
export interface TrainingLaunch {
  seed: number;
  tier: number;
  xpMult: number;
  mods?: SessionMods;
  /** The featured horse's coat — genetics made visible. */
  palette?: PaletteInstance;
}

export function launchPalette(launch: TrainingLaunch): PaletteInstance {
  return launch.palette ?? COAT_BAY;
}

export interface TrainingSceneOptions {
  onDone(tape: number[]): void;
}

const STEER_LEFT_CODES = ['ArrowLeft', 'KeyA'];
const STEER_RIGHT_CODES = ['ArrowRight', 'KeyD'];

export interface CollectorOptions {
  holdCodes?: string[];
  pressCodes?: string[];
  steer?: boolean;
}

export type TrainingControlAction = 'primary' | 'left' | 'right';
export type TrainingControlMode = 'hold' | 'press';

export interface TrainingControlSpec {
  action: TrainingControlAction;
  mode: TrainingControlMode;
  label: string;
  ariaLabel?: string;
}

/** Samples live input into one bitmask per sim tick. */
export class InputCollector {
  private readonly options: CollectorOptions;
  private readonly held = new Set<string>();
  private pressedQueue = 0;
  private leftPressedQueue = 0;
  private rightPressedQueue = 0;
  private readonly pointerHeld = new Map<TrainingControlAction, Set<number>>();
  private readonly cleanups: (() => void)[] = [];

  constructor(options: CollectorOptions) {
    this.options = options;
    const onKeyDown = (event: KeyboardEvent): void => {
      const { code } = event;
      if (options.pressCodes?.includes(code) && !event.repeat) {
        this.pressedQueue += 1;
        event.preventDefault();
      }
      if (options.steer && !event.repeat && STEER_LEFT_CODES.includes(code)) {
        this.leftPressedQueue += 1;
      }
      if (options.steer && !event.repeat && STEER_RIGHT_CODES.includes(code)) {
        this.rightPressedQueue += 1;
      }
      if (
        options.holdCodes?.includes(code) ||
        (options.steer && (STEER_LEFT_CODES.includes(code) || STEER_RIGHT_CODES.includes(code)))
      ) {
        this.held.add(code);
        event.preventDefault();
      }
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      this.held.delete(event.code);
    };
    const onBlur = (): void => {
      this.held.clear();
      for (const pointers of this.pointerHeld.values()) pointers.clear();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    this.cleanups.push(() => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    });
  }

  /** Bind an explicit on-screen control. Pointer input used to be attached
   * to the whole window, which made UI clicks count as drill actions and
   * left steering drills inaccessible on touch screens. */
  bindPointerControl(
    target: HTMLElement,
    action: TrainingControlAction,
    mode: TrainingControlMode,
  ): void {
    const activePointers = new Set<number>();
    this.pointerHeld.set(action, activePointers);
    const release = (event: PointerEvent): void => {
      activePointers.delete(event.pointerId);
      if (activePointers.size === 0) target.classList.remove('active');
    };
    const onDown = (event: PointerEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      if (mode === 'press') this.queuePress(action);
      else activePointers.add(event.pointerId);
      target.classList.add('active');
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        // Some browsers decline capture for synthetic pointer events. The
        // window-level release listener below still prevents sticky input.
      }
    };
    const onUp = (event: PointerEvent): void => {
      event.preventDefault();
      release(event);
    };
    const onWindowUp = (event: PointerEvent): void => release(event);
    target.addEventListener('pointerdown', onDown);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
    target.addEventListener('lostpointercapture', onUp);
    window.addEventListener('pointerup', onWindowUp);
    window.addEventListener('pointercancel', onWindowUp);
    this.cleanups.push(() => {
      target.removeEventListener('pointerdown', onDown);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      target.removeEventListener('lostpointercapture', onUp);
      window.removeEventListener('pointerup', onWindowUp);
      window.removeEventListener('pointercancel', onWindowUp);
    });
  }

  private queuePress(action: TrainingControlAction): void {
    if (action === 'primary') this.pressedQueue += 1;
    else if (action === 'left') this.leftPressedQueue += 1;
    else this.rightPressedQueue += 1;
  }

  /** One tick's bitmask; the press edge is consumed. */
  mask(): number {
    let mask = 0;
    if (this.pressedQueue > 0) {
      this.pressedQueue -= 1;
      mask |= MASK_PRESSED;
    }
    if (this.leftPressedQueue > 0) {
      this.leftPressedQueue -= 1;
      mask |= MASK_LEFT_PRESSED;
    }
    if (this.rightPressedQueue > 0) {
      this.rightPressedQueue -= 1;
      mask |= MASK_RIGHT_PRESSED;
    }
    const holdActive =
      (this.pointerHeld.get('primary')?.size ?? 0) > 0 ||
      (this.options.holdCodes?.some((code) => this.held.has(code)) ?? false);
    if (holdActive) mask |= MASK_HELD;
    if (this.options.steer) {
      if (
        STEER_LEFT_CODES.some((code) => this.held.has(code)) ||
        (this.pointerHeld.get('left')?.size ?? 0) > 0
      ) mask |= MASK_LEFT;
      if (
        STEER_RIGHT_CODES.some((code) => this.held.has(code)) ||
        (this.pointerHeld.get('right')?.size ?? 0) > 0
      ) mask |= MASK_RIGHT;
    }
    return mask;
  }

  dispose(): void {
    for (const cleanup of this.cleanups) cleanup();
  }
}

/** Visible, focusable controls for mouse, pen, and touch. Keyboard input
 * remains active in parallel, so these also make the control scheme clear
 * before the first cue arrives. */
export function trainingControls(
  collector: InputCollector,
  specs: readonly TrainingControlSpec[],
): HTMLElement {
  const wrap = el('div', { class: 'training-controls' });
  for (const spec of specs) {
    const button = el(
      'button',
      {
        class: `training-control interactive ${spec.action}`,
        type: 'button',
        'aria-label': spec.ariaLabel ?? spec.label,
      },
      spec.label,
    );
    collector.bindPointerControl(button, spec.action, spec.mode);
    wrap.append(button);
  }
  return wrap;
}

/** Fixed-step live driver: samples input per tick and records the tape
 * the sim will re-score. */
export class SessionRunner {
  readonly tape: number[] = [];
  private readonly clock = new DerbyClock(DRILL_TICK_S, 10);

  advance(nowMs: number, session: MinigameSession, collector: InputCollector): number {
    const frame = this.clock.advance(nowMs, 1, () => {
      if (session.finished) return;
      const mask = collector.mask();
      this.tape.push(mask);
      session.step(mask);
    });
    return frame.steps;
  }

  /** Player quit early: run the session out with no input so the scene's
   * final score matches the sim's replay of the same tape exactly. */
  fastForward(session: MinigameSession): void {
    let guard = 0;
    while (!session.finished && guard < 30 * 120) {
      session.step(0);
      guard += 1;
    }
  }
}

export interface TrainingResultsContent {
  title: string;
  detail: string;
  xp: number;
  failed: boolean;
}

export function showTrainingResults(
  root: HTMLElement,
  content: TrainingResultsContent,
  onDone: () => void,
): () => void {
  const panel = el(
    'div',
    { class: 'panel modal results' },
    el('div', { class: 'modal-title' }, content.failed ? `${content.title} — rough one` : content.title),
    el('div', { class: 'results-sub' }, content.detail),
    el('div', { class: 'drill-xp' }, `+${content.xp} XP`),
    el(
      'div',
      { class: 'modal-foot' },
      el('button', { class: 'button primary', onclick: onDone }, 'Back to the ranch (Enter)'),
    ),
  );
  const backdrop = el('div', { class: 'modal-backdrop' }, panel);
  root.append(backdrop);
  return () => backdrop.remove();
}
